import {
  fetchClients,
  fetchInvoice,
  fetchInvoiceSummary,
  fetchInvoices,
  linkInvoiceClient,
  syncXeroInvoices,
  undraftInvoice,
  type InvoiceSummary,
  type RemoteClient,
  type RemoteInvoice,
  type RemoteInvoiceDetail,
} from '../api/client.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';
import { isWideLayout, subscribeWideLayout } from './wideLayout.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPence(p: number | null | undefined): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

function statusLabel(s: string): string {
  if (s === 'draft') return 'Draft';
  if (s === 'sent') return 'Sent';
  if (s === 'paid') return 'Paid';
  return s;
}

function ageingBits(inv: RemoteInvoice): string {
  const bits: string[] = [];
  if (inv.status === 'paid' && inv.days_to_payment != null) {
    bits.push(`paid in ${inv.days_to_payment}d`);
  } else if (inv.days_since_sent != null) {
    bits.push(`${inv.days_since_sent}d since sent`);
  }
  if (inv.days_overdue != null && inv.days_overdue > 0) {
    bits.push(`${inv.days_overdue}d overdue`);
  }
  return bits.join(' · ');
}

type StatusChip = 'all' | 'open' | 'overdue' | 'paid';

function matchesChip(inv: RemoteInvoice, chip: StatusChip): boolean {
  if (chip === 'all') return true;
  if (chip === 'paid') return inv.status === 'paid';
  if (inv.not_in_xero) return false;
  if (chip === 'open') return inv.status === 'sent' || inv.status === 'draft';
  return (
    (inv.days_overdue ?? 0) > 0 &&
    inv.status === 'sent' &&
    inv.remaining_pence > 0
  );
}

function formatLastSynced(iso: string | null | undefined): string {
  if (!iso) return 'Not yet synced from Xero';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Not yet synced from Xero';
  return `Last synced ${d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export type MoneyHooks = {
  onOpenInvoice?: (id: string) => void;
  onChange?: () => void;
};

export async function mountMoney(
  root: HTMLElement,
  hooks: MoneyHooks = {},
): Promise<{
  refresh: () => Promise<void>;
  showInvoice: (id: string) => void;
  showOverdue: () => void;
}> {
  type View = 'list' | 'detail';
  let view: View = 'list';
  let detailId: string | null = null;
  let statusChip: StatusChip = 'all';
  let filtersOpen = false;
  let filterQ = '';
  let filterFrom = '';
  let filterTo = '';
  let cache: RemoteInvoice[] = [];
  let summary: InvoiceSummary | null = null;
  let listError = false;
  let hasLoadedOnce = false;
  let syncing = false;
  let syncNotice: { ok: boolean; text: string } | null = null;

  function ensureShell() {
    if (root.querySelector(':scope > .split-shell')) return;
    root.innerHTML = `
      <div class="split-shell">
        <div class="split-head"></div>
        <div class="split-layout">
          <div class="split-list"></div>
          <div class="split-detail"></div>
        </div>
      </div>`;
  }

  function shellEl(): HTMLElement {
    ensureShell();
    return root.querySelector(':scope > .split-shell') as HTMLElement;
  }

  function headEl(): HTMLElement {
    return shellEl().querySelector(':scope > .split-head') as HTMLElement;
  }

  function listEl(): HTMLElement {
    return shellEl().querySelector('.split-list') as HTMLElement;
  }

  function detailEl(): HTMLElement {
    return shellEl().querySelector('.split-detail') as HTMLElement;
  }

  function syncSplitClasses() {
    const wide = isWideLayout();
    root.classList.toggle('is-wide', wide);
    root.classList.toggle('is-phone-detail', !wide && view === 'detail');
  }

  function paintEmptyDetail() {
    if (!isWideLayout() || detailId) return;
    detailEl().innerHTML = `
      <section class="money-panel split-detail-empty">
        <p class="muted">Select an invoice</p>
      </section>`;
  }

  function markSelectedRow() {
    shellEl().querySelectorAll('[data-open]').forEach((el) => {
      const id = (el as HTMLElement).dataset.open;
      const on = Boolean(detailId && id === detailId);
      el.classList.toggle('is-selected', on);
      if (on) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }

  function showInvoice(id: string) {
    detailId = id;
    view = 'detail';
    markSelectedRow();
    syncSplitClasses();
    void renderDetail(id);
  }

  function showOverdue() {
    statusChip = 'overdue';
    view = 'list';
    detailId = null;
    void renderInvoices();
  }

  async function refresh() {
    if (isWideLayout()) {
      await renderInvoices({ preserveDetail: Boolean(detailId) });
      if (detailId) await renderDetail(detailId);
      return;
    }
    if (view === 'detail' && detailId) await renderDetail(detailId);
    else await renderInvoices();
  }

  async function loadInvoices(): Promise<void> {
    const query: Record<string, string> = { limit: '200' };
    if (filterQ) query.q = filterQ;
    if (filterFrom) query.from = filterFrom;
    if (filterTo) query.to = filterTo;
    try {
      const [inv, sum] = await Promise.all([
        fetchInvoices(query),
        fetchInvoiceSummary(),
      ]);
      cache = inv;
      summary = sum;
      listError = false;
      hasLoadedOnce = true;
    } catch {
      if (!hasLoadedOnce && cache.length === 0) listError = true;
    }
  }

  async function renderInvoices(opts?: { preserveDetail?: boolean }) {
    const preserveDetail = Boolean(opts?.preserveDetail && detailId);
    if (!preserveDetail) {
      view = 'list';
      detailId = null;
    } else if (view !== 'detail') {
      view = 'list';
    }

    ensureShell();
    syncSplitClasses();
    headEl().innerHTML = `
      <section class="money-panel">
        <h1 class="screen-title">Money</h1>
      </section>
    `;
    listEl().innerHTML = listSkeletonHtml(5, 'list-skel-row skel-money');
    if (!preserveDetail) paintEmptyDetail();
    await loadInvoices();

    const filtered = cache.filter((inv) => matchesChip(inv, statusChip));
    const counts = {
      all: cache.length,
      open: cache.filter((i) => matchesChip(i, 'open')).length,
      overdue: cache.filter((i) => matchesChip(i, 'overdue')).length,
      paid: cache.filter((i) => matchesChip(i, 'paid')).length,
    };

    const listBody = listError
      ? listErrorHtml()
      : filtered.length === 0
        ? listEmptyHtml(
            cache.length === 0
              ? 'No invoices yet. History fills in after import and as invoices are sent.'
              : 'No invoices match this filter.',
          )
        : `<ul class="money-list">
            ${filtered
              .map((inv) => {
                const ageing = ageingBits(inv);
                const overdue =
                  inv.days_overdue != null &&
                  inv.days_overdue > 0 &&
                  inv.status === 'sent' &&
                  inv.remaining_pence > 0;
                const shown =
                  inv.status === 'sent' ? inv.remaining_pence : inv.gross_pence;
                return `<li>
                  <button type="button" class="money-row ${overdue ? 'is-overdue' : ''}" data-open="${inv.id}">
                    <span class="money-row-top">
                      <strong>#${escapeHtml(inv.invoice_number ?? '—')}</strong>
                      <span>${formatPence(shown)}</span>
                    </span>
                    <span class="money-row-mid">${escapeHtml(inv.client_name)}</span>
                    <span class="money-row-bot muted">
                      ${escapeHtml(inv.invoice_date)} · ${escapeHtml(statusLabel(inv.status))}
                      ${inv.not_in_xero ? ' · Not in Xero' : ''}
                      ${inv.unmatched_contact ? ' · Unmatched contact' : ''}
                      ${inv.adapter === 'xero_import' ? ' · Raised in Xero' : ''}
                      ${ageing ? ` · ${escapeHtml(ageing)}` : ''}
                    </span>
                  </button>
                </li>`;
              })
              .join('')}
          </ul>`;

    headEl().innerHTML = `
      <section class="money-panel">
        <h1 class="screen-title">Money</h1>

        ${
          summary
            ? `<div class="money-summary" aria-label="Summary">
                <div class="money-summary-row">
                  <div>
                    <span class="muted">Outstanding</span>
                    <strong>${formatPence(summary.outstanding_pence)}</strong>
                  </div>
                  <div>
                    <span class="muted">Of which overdue</span>
                    <strong class="${summary.overdue_pence > 0 ? 'money-overdue' : ''}">${formatPence(summary.overdue_pence)}</strong>
                  </div>
                </div>
                <div class="money-summary-row">
                  <div>
                    <span class="muted">Invoiced this month</span>
                    <strong>${formatPence(summary.month.invoiced_pence)}</strong>
                  </div>
                  <div>
                    <span class="muted">${escapeHtml(summary.vat_quarter.label)}</span>
                    <strong>${formatPence(summary.vat_quarter.invoiced_pence)}</strong>
                  </div>
                </div>
                ${
                  summary.not_in_xero && summary.not_in_xero.pence > 0
                    ? `<div class="money-not-in-xero">
                        <div class="money-not-in-xero-head">
                          <span>Not in Xero</span>
                          <strong>${formatPence(summary.not_in_xero.pence)}</strong>
                        </div>
                        <p class="muted">On the books here, no matching invoice in Xero. Not in Outstanding or Overdue.</p>
                        <ul class="money-not-in-xero-list">
                          ${summary.not_in_xero.invoices
                            .map((inv) => {
                              const overdue =
                                inv.days_overdue != null && inv.days_overdue > 0
                                  ? ` · ${inv.days_overdue}d overdue`
                                  : '';
                              return `<li>
                                <button type="button" class="money-row" data-open="${inv.id}">
                                  <span class="money-row-top">
                                    <strong>#${escapeHtml(inv.invoice_number ?? '—')}</strong>
                                    <span>${formatPence(inv.remaining_pence)}</span>
                                  </span>
                                  <span class="money-row-mid">${escapeHtml(inv.client_name)}</span>
                                  <span class="money-row-bot muted">
                                    ${inv.due_date ? `Due ${escapeHtml(inv.due_date)}` : 'No due date'}${escapeHtml(overdue)}
                                  </span>
                                </button>
                              </li>`;
                            })
                            .join('')}
                        </ul>
                      </div>`
                    : ''
                }
                ${
                  summary.unmatched_contacts &&
                  summary.unmatched_contacts.length > 0
                    ? `<div class="money-unmatched">
                        <div class="money-unmatched-head">
                          <span>Unmatched Xero contacts</span>
                          <strong>${summary.unmatched_contacts.length}</strong>
                        </div>
                        <p class="muted">Imported from Xero. No local client yet — open one to link, or add the client from More first.</p>
                        <ul class="money-unmatched-list">
                          ${summary.unmatched_contacts
                            .map(
                              (inv) => `<li>
                                <button type="button" class="money-row" data-open="${inv.id}">
                                  <span class="money-row-top">
                                    <strong>#${escapeHtml(inv.invoice_number ?? '—')}</strong>
                                    <span>${formatPence(inv.remaining_pence)}</span>
                                  </span>
                                  <span class="money-row-mid">${escapeHtml(inv.client_name)}</span>
                                  <span class="money-row-bot muted">${
                                    inv.due_date
                                      ? `Due ${escapeHtml(inv.due_date)}`
                                      : 'No due date'
                                  }</span>
                                </button>
                              </li>`,
                            )
                            .join('')}
                        </ul>
                      </div>`
                    : ''
                }
                <div class="money-sync">
                  <p class="muted money-last-synced">${escapeHtml(formatLastSynced(summary.last_synced_at))}</p>
                  <button type="button" class="btn-secondary" data-xero-refresh ${syncing ? 'disabled' : ''} aria-busy="${syncing}">
                    ${syncing ? 'Refreshing…' : 'Refresh from Xero'}
                  </button>
                </div>
                ${
                  syncNotice
                    ? `<p class="${syncNotice.ok ? 'money-sync-ok' : 'money-sync-err'}" role="${syncNotice.ok ? 'status' : 'alert'}">${escapeHtml(syncNotice.text)}</p>`
                    : ''
                }
              </div>`
            : ''
        }

        <div class="filter-chips" role="group" aria-label="Invoice status">
          ${(['all', 'open', 'overdue', 'paid'] as StatusChip[])
            .map(
              (c) => `<button type="button" class="filter-chip" data-chip="${c}" aria-pressed="${statusChip === c}">
                ${c === 'all' ? 'All' : c === 'open' ? 'Open' : c === 'overdue' ? 'Overdue' : 'Paid'}
                <span class="chip-count">${counts[c]}</span>
              </button>`,
            )
            .join('')}
        </div>

        <div class="money-filter-toggle-row">
          <button type="button" class="btn-secondary btn-compact" data-toggle-filters aria-expanded="${filtersOpen}">
            ${filtersOpen ? 'Hide search & dates' : 'Search & dates'}
          </button>
        </div>
        ${
          filtersOpen
            ? `<div class="money-filters">
                <input type="search" placeholder="Invoice no. or client" data-q value="${escapeHtml(filterQ)}" />
                <div class="money-date-row">
                  <label>From <input type="date" data-from value="${filterFrom}" /></label>
                  <label>To <input type="date" data-to value="${filterTo}" /></label>
                </div>
              </div>`
            : ''
        }
      </section>
    `;

    listEl().innerHTML = `
      <section class="money-panel">
        ${listBody}
      </section>
    `;

    if (!preserveDetail) paintEmptyDetail();
    markSelectedRow();
    syncSplitClasses();

    const chrome = shellEl();
    chrome.querySelectorAll('[data-chip]').forEach((el) => {
      el.addEventListener('click', () => {
        statusChip = (el as HTMLElement).dataset.chip as StatusChip;
        void renderInvoices({ preserveDetail: isWideLayout() });
      });
    });
    chrome.querySelector('[data-toggle-filters]')?.addEventListener('click', () => {
      filtersOpen = !filtersOpen;
      void renderInvoices({ preserveDetail: isWideLayout() });
    });
    chrome.querySelector('[data-q]')?.addEventListener('change', (e) => {
      filterQ = (e.target as HTMLInputElement).value.trim();
      void renderInvoices({ preserveDetail: isWideLayout() });
    });
    chrome.querySelector('[data-from]')?.addEventListener('change', (e) => {
      filterFrom = (e.target as HTMLInputElement).value;
      void renderInvoices({ preserveDetail: isWideLayout() });
    });
    chrome.querySelector('[data-to]')?.addEventListener('change', (e) => {
      filterTo = (e.target as HTMLInputElement).value;
      void renderInvoices({ preserveDetail: isWideLayout() });
    });
    chrome.querySelectorAll('[data-open]').forEach((el) => {
      el.addEventListener('click', () => {
        showInvoice((el as HTMLElement).dataset.open!);
      });
    });
    chrome.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderInvoices({ preserveDetail });
    });
    chrome.querySelector('[data-xero-refresh]')?.addEventListener('click', () => {
      void runXeroRefresh();
    });
  }

  async function runXeroRefresh() {
    if (syncing) return;
    syncing = true;
    syncNotice = null;
    await renderInvoices({ preserveDetail: isWideLayout() });
    try {
      const r = await syncXeroInvoices();
      const failed = r.errors.length > 0;
      syncNotice = {
        ok: !failed,
        text: r.message,
      };
      hooks.onChange?.();
    } catch (err) {
      syncNotice = {
        ok: false,
        text: err instanceof Error ? err.message : 'Xero refresh failed',
      };
    } finally {
      syncing = false;
      await renderInvoices({ preserveDetail: isWideLayout() });
      if (isWideLayout() && detailId) await renderDetail(detailId);
    }
  }

  async function renderDetail(id: string) {
    view = 'detail';
    detailId = id;
    const root = detailEl();
    syncSplitClasses();
    let detail: RemoteInvoiceDetail | null;
    let clients: RemoteClient[] = [];
    try {
      detail = await fetchInvoice(id);
      if (detail?.unmatched_contact) {
        clients = await fetchClients();
      }
    } catch (err) {
      console.warn('invoice detail fetch failed', err);
      detail = null;
    }
    if (!detail) {
      root.innerHTML = `
        <section class="money-panel">
          <button type="button" class="btn-back" data-back>‹ Money</button>
          ${listErrorHtml('Couldn’t load this invoice. Check your connection and try again.')}
        </section>`;
      root.querySelector('[data-back]')?.addEventListener('click', () => {
        void renderInvoices();
      });
      root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
        void renderDetail(id);
      });
      return;
    }

    const ageing = ageingBits(detail);
    root.innerHTML = `
      <section class="money-panel">
        <button type="button" class="btn-back" data-back>‹ Money</button>
        <h1 class="screen-title">#${escapeHtml(detail.invoice_number ?? '—')}</h1>
        <p class="money-detail-head">
          <strong>${escapeHtml(detail.client_name)}</strong><br />
          <span class="muted">${escapeHtml(detail.invoice_date)} · ${escapeHtml(
            statusLabel(detail.status),
          )}          ${detail.not_in_xero ? ' · Not in Xero' : ''}${
            detail.unmatched_contact ? ' · Unmatched contact' : ''
          }${
            detail.adapter === 'xero_import' ? ' · Raised in Xero' : ''
          }${ageing ? ` · ${escapeHtml(ageing)}` : ''}</span>
        </p>
        ${
          detail.not_in_xero
            ? `<div class="money-not-in-xero-banner">
                <p>No matching invoice in Xero. Left visible so you can check the bank — not counted in Outstanding or Overdue.</p>
              </div>`
            : ''
        }
        ${
          detail.unmatched_contact
            ? `<div class="money-unmatched-banner">
                <p>Xero contact <strong>${escapeHtml(detail.client_name)}</strong> has no local client. The invoice still counts. Link an existing client, or add one from More first — nothing is created automatically.</p>
                <label class="money-link-client">
                  <span class="muted">Link to client</span>
                  <select data-link-client>
                    <option value="">Choose…</option>
                    ${clients
                      .map(
                        (c) =>
                          `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`,
                      )
                      .join('')}
                  </select>
                </label>
                <button type="button" class="btn-secondary" data-link-client-save disabled>Link client</button>
              </div>`
            : ''
        }
        <div class="money-amounts">
          <div><span class="muted">Net</span><strong>${formatPence(detail.net_pence)}</strong></div>
          <div><span class="muted">VAT</span><strong>${formatPence(detail.vat_pence)}</strong></div>
          <div><span class="muted">Gross</span><strong>${formatPence(detail.gross_pence)}</strong></div>
          ${
            detail.status === 'sent'
              ? `<div><span class="muted">Remaining</span><strong>${formatPence(detail.remaining_pence)}</strong></div>`
              : ''
          }
        </div>
        ${
          detail.due_date
            ? `<p class="muted">Due ${escapeHtml(detail.due_date)}${
                detail.paid_at ? ` · Paid ${escapeHtml(detail.paid_at)}` : ''
              }</p>`
            : ''
        }
        <h2 class="money-subhead">Lines</h2>
        ${
          detail.lines.length === 0
            ? `<p class="muted">No line items linked yet.</p>`
            : `<ul class="money-lines">
                ${detail.lines
                  .map(
                    (L) => `<li>
                      <div>
                        <strong>${escapeHtml(L.description)}</strong>
                        ${
                          L.work_date
                            ? `<span class="muted"> · ${escapeHtml(L.work_date)}</span>`
                            : ''
                        }
                      </div>
                      <span>${formatPence(L.net_pence)}</span>
                    </li>`,
                  )
                  .join('')}
              </ul>`
        }
        ${
          detail.status === 'draft'
            ? `<div class="money-undraft">
                <p class="hint">Xero draft. If you deleted it in Xero, return these jobs to unreleased so they come back into the week review. This does not delete anything in Xero.</p>
                <button type="button" class="btn-secondary" data-undraft>Return jobs to unreleased</button>
              </div>`
            : ''
        }
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      void renderInvoices();
    });
    const linkSelect = root.querySelector(
      '[data-link-client]',
    ) as HTMLSelectElement | null;
    const linkBtn = root.querySelector(
      '[data-link-client-save]',
    ) as HTMLButtonElement | null;
    linkSelect?.addEventListener('change', () => {
      if (linkBtn) linkBtn.disabled = !linkSelect.value;
    });
    linkBtn?.addEventListener('click', () => {
      void (async () => {
        const clientId = linkSelect?.value;
        if (!clientId) return;
        linkBtn.disabled = true;
        try {
          await linkInvoiceClient(id, clientId);
          hooks.onChange?.();
          await renderInvoices({ preserveDetail: true });
          await renderDetail(id);
        } catch (err) {
          linkBtn.disabled = false;
          window.alert(
            err instanceof Error
              ? err.message
              : 'Could not link this client — unlock online and try again.',
          );
        }
      })();
    });
    root.querySelector('[data-undraft]')?.addEventListener('click', () => {
      void (async () => {
        const n = detail.lines.filter((L) => L.id).length;
        const ok = window.confirm(
          `Return ${n === 1 ? 'this job' : `${n || 'these'} jobs`} to unreleased?\n\nThey will leave this draft and come back into the week review. Delete the invoice in Xero first if it is still there.`,
        );
        if (!ok) return;
        const btn = root.querySelector('[data-undraft]') as HTMLButtonElement;
        btn.disabled = true;
        try {
          const result = await undraftInvoice(id);
          hooks.onChange?.();
          window.alert(
            result.jobs === 1
              ? '1 job is unreleased again.'
              : `${result.jobs} jobs are unreleased again.`,
          );
          await renderInvoices();
        } catch (err) {
          btn.disabled = false;
          window.alert(
            err instanceof Error
              ? err.message
              : 'Could not un-draft — unlock online and try again.',
          );
        }
      })();
    });
  }

  subscribeWideLayout(() => {
    syncSplitClasses();
    void refresh();
  });

  await renderInvoices();
  return { refresh, showInvoice, showOverdue };
}
